import * as React from "react";
import { withTranslation, WithTranslation, Trans } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Input, Switch, Button, Select, Row, Col, Popover, Form, Alert } from "antd";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MetadataRangeList } from "./MetadataRangeList";
import { FormInstance, RuleObject } from "antd/lib/form";
import { metadataStore } from "../../../Stores/MetadataStore";
import { observer } from "mobx-react";
const FormItem = Form.Item;

interface MetaDataFormProps extends WithTranslation, ModalComponentProps {
  onEdit: (obj: API_Metadata) => Promise<void>;
  onCreate: (obj: API_Metadata) => Promise<void>;
  initialData?: API_Metadata;
  readOnly?: boolean;
}

interface MetaDataFormState {
  showRangeList: boolean;
  nameInputDisabled: boolean;
}

export const MetaDataForm = withTranslation(I18N_NS.ADMIN)(
  observer(
    class extends React.Component<MetaDataFormProps, MetaDataFormState> {
      private formRef = React.createRef<FormInstance>();
      constructor(props: MetaDataFormProps) {
        super(props);
        this.state = {
          showRangeList: false,
          nameInputDisabled: false,
        };
      }

      componentDidMount() {
        if (this.props.initialData) {
          this.processFormatValue(this.props.initialData.format);
          this.setState({ nameInputDisabled: this.props.initialData.protectedName });
        }
      }

      // This functionality is based on the classic version: if provided value = W - then show the rangeList inputs
      processFormatValue = (value: string) => {
        if (value && value.toLowerCase() === "w") {
          this.setState({ showRangeList: true });
        } else {
          this.setState({ showRangeList: false });
        }
      };

      handleChangeValues = (changedValues: API_Metadata) => {
        if (Object.prototype.hasOwnProperty.call(changedValues, "protectedName")) {
          this.setState({ nameInputDisabled: changedValues.protectedName });
        }
        if (Object.keys(changedValues).length > 0) {
          this.props.setConfirmOnCancel(true);
        }
      };

      handleSubmit = async (values) => {
        if (this.props.readOnly) {
          return;
        }
        try {
          if (!this.props.initialData) {
            await this.props.onCreate(values);
          } else {
            await this.props.onEdit({ ...this.props.initialData, ...values });
          }
          this.props.setConfirmOnCancel(false);
          this.props.hideModal();
          await metadataStore.fetch();
        } catch (error) {
          throw error;
        }
      };

      rangeListValidator: RuleObject["validator"] = (rule, value: API_MetadataValueRange[] = []) => {
        // check if all metadata values are unique
        const set = new Set<string>(value.map((i) => i.value));
        if (set.size !== value.length) {
          return Promise.reject(this.props.t("Values must be unique"));
        }
        return Promise.resolve();
      };

      get suffix() {
        return (
          <Popover
            content={
              <>
                <Trans>
                  <div>
                    <div>* = All values allowed or</div>
                    <div>DATE = Date value allowed or </div>
                    <div>W = value range or </div>
                  </div>
                  <div>assignment with a default value:</div>
                  <ul>
                    <li>{`{USERID} = UserID of the user`}</li>
                    <li>{`{USERNAME} = name of the user`}</li>
                    <li>{`{USERFIRMA} = company of the user`}</li>
                    <li>{`{DATE} = Current date`}</li>
                    <li>{`{'xxxx'} = Any string`}</li>
                  </ul>
                </Trans>
              </>
            }
          >
            <span>
              <FontAwesomeIcon icon={["far", "info-circle"]} fixedWidth={true} />
            </span>
          </Popover>
        );
      }

      render() {
        const { t, readOnly } = this.props;
        const initialValues = this.props.initialData
          ? this.props.initialData
          : { active: true, type: 0, protectedName: true, sysMetadata: false };
        const isEditMode = !!this.props.initialData;

        return (
          <Form
            ref={this.formRef}
            initialValues={initialValues}
            layout="vertical"
            disabled={readOnly}
            onFinish={this.handleSubmit}
            onValuesChange={this.handleChangeValues}
          >
            {metadataStore.mode === "ADMIN" && (
              <Row gutter={16} className="mb-sm">
                <Col flex="1 1 0">
                  <FormItem name="required" label={t("Required")} valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Switch />
                  </FormItem>
                </Col>
                <Col flex="1 1 0">
                  <FormItem name="active" label={t("Active")} valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Switch />
                  </FormItem>
                </Col>
                <Col flex="1 1 0">
                  <FormItem
                    name="show"
                    label={t("Show for search")}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </FormItem>
                </Col>
                <Col flex="1 1 0">
                  <FormItem
                    name="protectedName"
                    label={t("Protected name")}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </FormItem>
                </Col>
                <Col flex="1 1 0">
                  <FormItem
                    name="sysMetadata"
                    label={t("Sys-Metadata")}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </FormItem>
                </Col>
              </Row>
            )}
            <FormItem name="key" label={t("Name")} rules={[{ required: true, message: t("This field is required") }]}>
              {/* Disable input on edit, enable on create to set the initial value */}
              <Input disabled={readOnly || (isEditMode && this.state.nameInputDisabled)} />
            </FormItem>
            {readOnly && (
              <Alert
                className="mb-md"
                type="info"
                showIcon
                message={t("This metadata is protected. Contact support for any change")}
              />
            )}
            <FormItem name="description" label={t("Description")}>
              <Input />
            </FormItem>
            {metadataStore.mode === "ADMIN" && (
              <FormItem
                name="format"
                label={t("Format")}
                rules={[{ required: true, message: t("This field is required") }]}
                getValueFromEvent={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  this.processFormatValue(value);
                  return value;
                }}
              >
                <Input suffix={this.suffix} />
              </FormItem>
            )}
            {this.state.showRangeList && (
              <FormItem
                name="rangeList"
                label={t("Value range")}
                rules={[
                  { required: true, message: t("This field is required") },
                  { validator: this.rangeListValidator },
                ]}
              >
                <MetadataRangeList readOnly={readOnly} />
              </FormItem>
            )}
            {metadataStore.mode === "ADMIN" && (
              <FormItem name="type" label={t("Type")} rules={[{ required: true }]}>
                <Select>
                  <Select.Option key="0" value={0}>
                    {t("Manual")}
                  </Select.Option>
                  <Select.Option key="1" value={1}>
                    {t("Automatic")}
                  </Select.Option>
                </Select>
              </FormItem>
            )}
            <ButtonsRow justify="space-between">
              <Button onClick={this.props.hideModal}>{readOnly ? t("Close") : t("Cancel")}</Button>
              {!readOnly && (
                <Button type="primary" loading={metadataStore.state === "LOADING"} htmlType="submit">
                  {t("Save")}
                </Button>
              )}
            </ButtonsRow>
          </Form>
        );
      }
    }
  )
);
